import { useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { FileText, TrendingUp, Package, Download } from 'lucide-react';
import { formatCurrency } from '../utils/formatters';

const Reports = ({ user }) => {
  const [loading, setLoading] = useState(false);
  const [salesReport, setSalesReport] = useState(null);
  const [inventoryReport, setInventoryReport] = useState(null);
  const [profitabilityReport, setProfitabilityReport] = useState(null);
  const [dateRange, setDateRange] = useState({
    fecha_inicio: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
    fecha_fin: new Date().toISOString().split('T')[0],
  });

  const formatDateParam = (dateStr) => {
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  };

  const fetchSalesReport = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/reports/sales`, {
        params: {
          fecha_inicio: formatDateParam(dateRange.fecha_inicio),
          fecha_fin: formatDateParam(dateRange.fecha_fin),
        },
      });
      setSalesReport(response.data);
      toast.success('Reporte de ventas generado');
    } catch (error) {
      toast.error('Error al generar reporte');
    } finally {
      setLoading(false);
    }
  };

  const fetchInventoryReport = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/reports/inventory`);
      setInventoryReport(response.data);
      toast.success('Reporte de inventario generado');
    } catch (error) {
      toast.error('Error al generar reporte');
    } finally {
      setLoading(false);
    }
  };

  const fetchProfitabilityReport = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/reports/profitability`, {
        params: {
          fecha_inicio: formatDateParam(dateRange.fecha_inicio),
          fecha_fin: formatDateParam(dateRange.fecha_fin),
        },
      });
      setProfitabilityReport(response.data);
      toast.success('Reporte de rentabilidad generado');
    } catch (error) {
      toast.error('Error al generar reporte');
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = (data, filename) => {
    const csvContent = data;
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h1 className="text-4xl font-bold text-slate-800">Reportes</h1>
        <p className="text-slate-600 mt-1">Genera reportes de ventas, inventario y rentabilidad</p>
      </div>

      <Tabs defaultValue="sales" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="sales" data-testid="tab-sales">
            <FileText className="mr-2" size={18} />
            Ventas
          </TabsTrigger>
          <TabsTrigger value="inventory" data-testid="tab-inventory">
            <Package className="mr-2" size={18} />
            Inventario
          </TabsTrigger>
          <TabsTrigger value="profitability" data-testid="tab-profitability">
            <TrendingUp className="mr-2" size={18} />
            Rentabilidad
          </TabsTrigger>
        </TabsList>

        {/* Sales Report */}
        <TabsContent value="sales" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Reporte de Ventas</CardTitle>
              <CardDescription>Ventas por período con desglose por forma de pago</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="fecha_inicio">Fecha Inicio</Label>
                    <Input
                      id="fecha_inicio"
                      type="date"
                      value={dateRange.fecha_inicio}
                      onChange={(e) => setDateRange({ ...dateRange, fecha_inicio: e.target.value })}
                      data-testid="fecha-inicio-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="fecha_fin">Fecha Fin</Label>
                    <Input
                      id="fecha_fin"
                      type="date"
                      value={dateRange.fecha_fin}
                      onChange={(e) => setDateRange({ ...dateRange, fecha_fin: e.target.value })}
                      data-testid="fecha-fin-input"
                    />
                  </div>
                </div>
                <Button
                  onClick={fetchSalesReport}
                  disabled={loading}
                  className="bg-emerald-500 hover:bg-emerald-600"
                  data-testid="generate-sales-btn"
                >
                  {loading ? 'Generando...' : 'Generar Reporte'}
                </Button>
              </div>

              {salesReport && (
                <div className="mt-6 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <div className="text-sm text-slate-600">Total Ventas</div>
                      <div className="text-2xl font-bold text-blue-600">{formatCurrency(salesReport.total_ventas)}</div>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <div className="text-sm text-slate-600">Subtotal</div>
                      <div className="text-2xl font-bold text-green-600">{formatCurrency(salesReport.total_subtotal)}</div>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <div className="text-sm text-slate-600">ITBIS</div>
                      <div className="text-2xl font-bold text-purple-600">{formatCurrency(salesReport.total_itbis)}</div>
                    </div>
                    <div className="bg-amber-50 p-4 rounded-lg">
                      <div className="text-sm text-slate-600">Facturas</div>
                      <div className="text-2xl font-bold text-amber-600">{salesReport.total_facturas}</div>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold mb-3">Ventas por Forma de Pago</h3>
                    <div className="grid grid-cols-3 gap-4">
                      {Object.entries(salesReport.ventas_por_tipo_pago).map(([tipo, monto]) => (
                        <div key={tipo} className="bg-slate-50 p-4 rounded-lg">
                          <div className="text-sm text-slate-600 capitalize">{tipo}</div>
                          <div className="text-xl font-bold">{formatCurrency(monto)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Inventory Report */}
        <TabsContent value="inventory" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Reporte de Inventario</CardTitle>
              <CardDescription>Valorización y estado del inventario</CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                onClick={fetchInventoryReport}
                disabled={loading}
                className="bg-emerald-500 hover:bg-emerald-600"
                data-testid="generate-inventory-btn"
              >
                {loading ? 'Generando...' : 'Generar Reporte'}
              </Button>

              {inventoryReport && (
                <div className="mt-6 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <div className="text-sm text-slate-600">Total Productos</div>
                      <div className="text-2xl font-bold text-blue-600">{inventoryReport.total_products}</div>
                    </div>
                    <div className="bg-red-50 p-4 rounded-lg">
                      <div className="text-sm text-slate-600">Stock Bajo</div>
                      <div className="text-2xl font-bold text-red-600">{inventoryReport.low_stock_count}</div>
                    </div>
                    <div className="bg-amber-50 p-4 rounded-lg">
                      <div className="text-sm text-slate-600">Sin Stock</div>
                      <div className="text-2xl font-bold text-amber-600">{inventoryReport.out_of_stock}</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <div className="text-sm text-slate-600">Valor Costo</div>
                      <div className="text-xl font-bold">{formatCurrency(inventoryReport.total_costo_inventario)}</div>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <div className="text-sm text-slate-600">Valor Venta</div>
                      <div className="text-xl font-bold text-green-600">{formatCurrency(inventoryReport.total_valor_venta)}</div>
                    </div>
                    <div className="bg-emerald-50 p-4 rounded-lg">
                      <div className="text-sm text-slate-600">Utilidad Potencial</div>
                      <div className="text-xl font-bold text-emerald-600">{formatCurrency(inventoryReport.utilidad_potencial)}</div>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold mb-3">Por Categoría</h3>
                    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                      <table className="custom-table">
                        <thead>
                          <tr>
                            <th>Categoría</th>
                            <th>Cantidad</th>
                            <th>Valor Costo</th>
                            <th>Valor Venta</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(inventoryReport.por_categoria).map(([cat, data]) => (
                            <tr key={cat}>
                              <td className="font-medium">{cat}</td>
                              <td>{data.cantidad}</td>
                              <td>{formatCurrency(data.valor_costo)}</td>
                              <td className="text-green-600 font-semibold">{formatCurrency(data.valor_venta)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Profitability Report */}
        <TabsContent value="profitability" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Reporte de Rentabilidad</CardTitle>
              <CardDescription>Análisis de rentabilidad por producto</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="fecha_inicio_profit">Fecha Inicio</Label>
                    <Input
                      id="fecha_inicio_profit"
                      type="date"
                      value={dateRange.fecha_inicio}
                      onChange={(e) => setDateRange({ ...dateRange, fecha_inicio: e.target.value })}
                      data-testid="profit-fecha-inicio-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="fecha_fin_profit">Fecha Fin</Label>
                    <Input
                      id="fecha_fin_profit"
                      type="date"
                      value={dateRange.fecha_fin}
                      onChange={(e) => setDateRange({ ...dateRange, fecha_fin: e.target.value })}
                      data-testid="profit-fecha-fin-input"
                    />
                  </div>
                </div>
                <Button
                  onClick={fetchProfitabilityReport}
                  disabled={loading}
                  className="bg-emerald-500 hover:bg-emerald-600"
                  data-testid="generate-profit-btn"
                >
                  {loading ? 'Generando...' : 'Generar Reporte'}
                </Button>
              </div>

              {profitabilityReport && (
                <div className="mt-6 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <div className="text-sm text-slate-600">Total Ventas</div>
                      <div className="text-xl font-bold text-blue-600">{formatCurrency(profitabilityReport.total_ventas)}</div>
                    </div>
                    <div className="bg-red-50 p-4 rounded-lg">
                      <div className="text-sm text-slate-600">Total Costos</div>
                      <div className="text-xl font-bold text-red-600">{formatCurrency(profitabilityReport.total_costos)}</div>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <div className="text-sm text-slate-600">Utilidad Total</div>
                      <div className="text-xl font-bold text-green-600">{formatCurrency(profitabilityReport.utilidad_total)}</div>
                    </div>
                    <div className="bg-emerald-50 p-4 rounded-lg">
                      <div className="text-sm text-slate-600">Margen Promedio</div>
                      <div className="text-xl font-bold text-emerald-600">{profitabilityReport.margen_promedio.toFixed(2)}%</div>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold mb-3">Rentabilidad por Producto</h3>
                    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                      <div className="overflow-x-auto max-h-96">
                        <table className="custom-table">
                          <thead>
                            <tr>
                              <th>Producto</th>
                              <th>Cantidad</th>
                              <th>Ventas</th>
                              <th>Costos</th>
                              <th>Utilidad</th>
                              <th>Margen %</th>
                            </tr>
                          </thead>
                          <tbody>
                            {profitabilityReport.productos
                              .sort((a, b) => b.utilidad - a.utilidad)
                              .map((product, idx) => (
                                <tr key={idx}>
                                  <td className="font-medium">{product.nombre}</td>
                                  <td>{product.cantidad_vendida}</td>
                                  <td>{formatCurrency(product.ventas_totales)}</td>
                                  <td className="text-red-600">{formatCurrency(product.costo_total)}</td>
                                  <td className="font-semibold text-green-600">{formatCurrency(product.utilidad)}</td>
                                  <td className="font-semibold">{product.margen.toFixed(2)}%</td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Reports;
